<template lang="pug">
ActivitiesLocationInfoWindow.activities-location-member-info-window(
  :class="{ active, loading }"
  :map="map"
  :report-url="memberReportUrl"
  @close="onClose"
)
  template(#updated-at)
    span.sk.sk--line(v-if="loading")
    template(v-else)
      | {{ lastUpdatedAt }}
  template(#updated-btn)
    ActivitiesLocationViewButton.view-route-btn(
      text="routes"
      icon="hi-route"
      :active="activeRoute"
      @click="$emit('view-route')"
    )
  template(#avatar)
    HubAvatar.hidden-xs(
      :user-id="marker.id"
      avatar-style="thumb"
    )
    span(
      v-if="!isPastView"
      :class="workingClass"
    )
  template(#primary-detail)
    strong.break-word {{ marker.name }}
  template(#secondary-detail)
    .sk.sk--line(v-if="loading")
    template(v-else)
      | {{ projectName }}
  template(#tertiary-detail)
    .sk.sk--line(v-if="loading")
    template(v-else)
      | {{ taskName }}
  template(#cell-one)
    .info-window-label {{ workedLabel }}
    .sk.sk--line(v-if="loading")
    .info-window-last-worked(v-else)
      | {{ formattedLastWorked }}
  template(#cell-three)
    .info-window-label JOB SITE VISITS
    .sk.sk--line(v-if="loading")
    .info-window-site-visits-count(v-else) {{ jobSiteVisitsCount }}
  template(#list-label)
    .sk.sk--line.sk--line--quarter(v-if="loading")
    template(v-else)
      | {{ `JOB SITE VISITS (${jobSiteVisitsCount})` }}
  template(#list-items)
    template(v-if="loading")
      .col-xs-12.d-flex(
        v-for="n in 3"
        :key="'sk-' + n"
      )
        .sk.sk--circle.sk--circle--xs.mr-5
        .sk.sk--line.sk--line--half.mr-30
        .sk.sk--line.sk--line--half
    template(v-else)
      ActivitiesLocationInfoWindowListItem(
        v-for="visit in jobSiteVisitsToDisplay"
        :key="visit.id"
        :job-site-visit="visit"
        :zone="activeTimeZone.name"
        :emit-id="visit.site.id"
        @click="onListItemClick"
      )
        template(#name)
          | {{ visit.site.name }}
        template(#avatar)
          img.job-site-marker(:src="generateCustomColorMarker(visit.site.color, true)")
  template(#list-and-more)
    .sk.sk--line.sk--line--quarter(v-if="loading")
    template(v-else)
      .col-xs-12.and-more(v-if="jobSiteVisitsCount > 3")
        | {{ `and ${jobSiteVisitsCountExcess} more` }}
</template>

<script>
import { get, omit, isNil } from 'lodash-es';
import { mapActions, mapGetters, mapState } from 'vuex';
import { generateMarker } from '~/mixins';
import { formatTime, formatDuration } from '~/utils/time_utils';
import { getTaskFlavourLabel, getProjectFlavourLabel } from '~/utils/project_flavour';

export default {
  name: 'ActivitiesLocationMemberInfoWindow',
  mixins: [generateMarker],
  props: {
    marker: {
      type: Object,
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
    activeRoute: Boolean,
    map: {
      type: Object,
      default: null,
    },
  },
  emits: ['view-route', 'close', 'click'],
  data: () => ({
    visits: [],
    details: null,
  }),
  computed: {
    ...mapState(['sites', 'tasks', 'projects', 'organization']),
    ...mapState({
      loading: (state) => get(state, 'loading.userDetails', false),
    }),
    ...mapGetters(['memberReportUrl', 'isPastView', 'activeTimeZone']),
    workingClass() {
      return {
        status: true,
        'hidden-xs': true,
        'status-active': this.marker.activeNow,
      };
    },
    workedLabel() {
      return (this.isPastView ? 'Last worked at' : 'Worked today').toUpperCase();
    },
    jobSiteVisits() {
      return get(this, 'visits', []).map((visit) => ({
        ...omit(visit, ['site_id']),
        site: !isNil(visit.site_id) ? this.sites[visit.site_id] : null,
      }));
    },
    jobSiteVisitsToDisplay() {
      return this.jobSiteVisits.slice(0, 3);
    },
    jobSiteVisitsCount() {
      return this.jobSiteVisits.length;
    },
    jobSiteVisitsCountExcess() {
      return this.jobSiteVisitsCount - 3;
    },
    lastUpdatedAt() {
      if (!this.details) {
        return null;
      }

      const tz = get(this.activeTimeZone, 'name');
      const lastLocationUpdatedAt = get(this.details, 'last_location_updated_at');
      const lastUpdatedAt = tz ? moment.tz(lastLocationUpdatedAt, tz) : moment(lastLocationUpdatedAt);

      return lastUpdatedAt.fromNow();
    },
    lastWorked() {
      if (!this.details) {
        return null;
      }

      const tz = get(this.activeTimeZone, 'name');
      const lastWorkedAt = get(this.details, 'last_worked_at');

      return lastWorkedAt ? (tz ? moment.tz(lastWorkedAt, tz) : moment(lastWorkedAt)) : null;
    },
    formattedDuration() {
      return this.details ? formatDuration(this.details.total_tracked, { blank: '-' }) : '-';
    },
    formattedLastWorked() {
      if (this.marker.activeNow) {
        return 'NOW';
      }

      return this.lastWorked ? formatTime(this.lastWorked, { showSeconds: 'never' }) : '-';
    },
    decoratedDetails() {
      if (!this.details) {
        return null;
      }

      const { task_id: taskId, project_id: projectId } = this.details;

      return !this.details
        ? null
        : Object.assign({}, omit(this.details, ['task_id', 'project_id']), {
            task: !isNil(taskId) ? this.tasks[taskId] : null,
            project: !isNil(projectId) ? this.projects[projectId] : null,
          });
    },
    taskName() {
      const taskName = get(this.decoratedDetails, 'task.summary');

      return taskName || this.noTaskText;
    },
    projectName() {
      const projectName = get(this.decoratedDetails, 'project.name');

      return projectName || this.noProjectText;
    },
    noTaskText() {
      const taskLabel = getTaskFlavourLabel(this.organization.project_flavour);

      return `No ${taskLabel.toLowerCase()}`;
    },
    noProjectText() {
      const projectLabel = getProjectFlavourLabel(this.organization.project_flavour);

      return `No ${projectLabel.toLowerCase()}`;
    },
  },
  async created() {
    await this.loadDetails();
  },
  unmounted() {
    this.visits = [];
    this.details = null;
  },
  methods: {
    ...mapActions(['loadUserDetails']),
    onListItemClick(id) {
      this.$emit('close');
      this.$emit('click', id);
    },
    onClose() {
      this.$emit('close');
    },
    async loadDetails() {
      const { id } = this.marker;
      const response = await this.loadUserDetails(id);

      if (!response) {
        this.$appMessage('Error loading user details');

        return;
      }

      this.visits = get(response, 'visits', []);
      this.details = get(response, 'details', null);
    },
  },
};
</script>
